import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Description as FileIcon,
  ChevronRight as ChevronRightIcon,
  ExpandMore as ExpandMoreIcon,
  AccountTree as AccountTreeIcon,
  AttachMoney as MoneyIcon,
  AccountBalance as BalanceIcon,
  ShowChart as ChartIcon,
} from '@mui/icons-material';

const TreeContainer = styled.div`
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  max-width: 100%;
  margin: 0 auto;
  padding: 1rem;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
`;

const TreeHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem 1.5rem;
  background: linear-gradient(135deg, #4a6cf7 0%, #2541b2 100%);
  color: white;
  border-radius: 10px 10px 0 0;
  margin-bottom: 1rem;
  
  h2 {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }
`;

const TreeNode = styled(motion.li)`
  list-style: none;
  margin: 0.25rem 0;
  padding-left: ${props => props.level * 1.5}rem;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    left: 0.5rem;
    top: 1.5rem;
    height: calc(100% - 1.5rem);
    width: 1px;
    background: #e0e0e0;
  }
  
  &:last-child::before {
    height: 1rem;
  }
`;

const NodeContent = styled(motion.div)`
  display: flex;
  align-items: center;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.isSelected ? '#f0f4ff' : 'transparent'};
  
  &:hover {
    background: #f8f9ff;
  }
  
  .account-icon {
    margin-right: 0.75rem;
    color: #4a6cf7;
    font-size: 1.25rem;
  }
  
  .toggle-icon {
    margin-right: 0.5rem;
    color: #6c757d;
    transition: transform 0.2s ease;
    transform: ${props => props.isExpanded ? 'rotate(90deg)' : 'rotate(0)'};
  }
  
  .account-name {
    flex: 1;
    font-size: 0.95rem;
    color: #2d3748;
    font-weight: ${props => props.isSelected ? '600' : '400'};
  }
  
  .account-code {
    background: #edf2f7;
    padding: 0.2rem 0.5rem;
    border-radius: 12px;
    font-size: 0.75rem;
    color: #4a5568;
    margin-left: 0.5rem;
  }
  
  .account-balance {
    margin-left: 1rem;
    font-size: 0.85rem;
    color: #4a5568;
    font-weight: 500;
  }
`;

const TreeList = styled.ul`
  padding: 0;
  margin: 0;
  list-style: none;
`;

const AccountIcon = ({ type, level }) => {
  const size = level === 0 ? '1.5rem' : '1.25rem';
  
  if (level === 0) {
    return <BalanceIcon className="account-icon" style={{ fontSize: size }} />;
  }
  
  switch(type) {
    case 'income':
      return <MoneyIcon className="account-icon" style={{ color: '#10B981', fontSize: size }} />;
    case 'expense':
      return <MoneyIcon className="account-icon" style={{ color: '#EF4444', fontSize: size }} />;
    case 'asset':
      return <AccountTreeIcon className="account-icon" style={{ color: '#3B82F6', fontSize: size }} />;
    case 'liability':
      return <ChartIcon className="account-icon" style={{ color: '#F59E0B', fontSize: size }} />;
    default:
      return <FileIcon className="account-icon" style={{ fontSize: size }} />;
  }
};

const TreeNodeComponent = ({ 
  node, 
  level = 0, 
  onSelect, 
  selectedNode,
  expandedNodes,
  toggleNode 
}) => {
  const hasChildren = node.children && node.children.length > 0;
  const isExpanded = expandedNodes.includes(node.Code);
  const isSelected = selectedNode && selectedNode.Code === node.Code;
  
  // Determine account type based on code or other criteria
  const getAccountType = (code) => {
    if (code < 2000) return 'asset';
    if (code < 3000) return 'liability';
    if (code < 4000) return 'equity';
    if (code < 5000) return 'income';
    return 'expense';
  };
  
  const accountType = getAccountType(node.Code);
  
  return (
    <TreeNode 
      level={level}
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.2 }}
    >
      <NodeContent 
        isSelected={isSelected}
        isExpanded={isExpanded}
        onClick={() => {
          onSelect(node);
          if (hasChildren) toggleNode(node.Code);
        }}
      >
        {hasChildren ? (
          isExpanded ? (
            <ExpandMoreIcon className="toggle-icon" />
          ) : (
            <ChevronRightIcon className="toggle-icon" />
          )
        ) : (
          <div style={{ width: '24px' }} />
        )}
        
        <AccountIcon type={accountType} level={level} />
        
        <span className="account-name">
          {node.Name || `Account ${node.Code}`}
          <span className="account-code">{node.Code}</span>
        </span>
        
        {(node.BopDbt || node.BopCrdt) && (
          <span className="account-balance">
            {node.BopDbt > 0 ? `دينار ${node.BopDbt.toLocaleString()}` : ''}
            {node.BopCrdt > 0 ? `دينار ${node.BopCrdt.toLocaleString()}` : ''}
          </span>
        )}
      </NodeContent>
      
      <AnimatePresence>
        {hasChildren && isExpanded && (
          <TreeList
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            {node.children.map(childNode => (
              <TreeNodeComponent
                key={childNode.Code}
                node={childNode}
                level={level + 1}
                onSelect={onSelect}
                selectedNode={selectedNode}
                expandedNodes={expandedNodes}
                toggleNode={toggleNode}
              />
            ))}
          </TreeList>
        )}
      </AnimatePresence>
    </TreeNode>
  );
};

const ModernAccountTreeView = ({ data, onSelectAccount, initialExpandedLevels = 2 }) => {
  const [expandedNodes, setExpandedNodes] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  
  // Expand nodes up to the specified level initially
  useEffect(() => {
    if (!data) return;
    const ids = [];
    const collect = (nodes, level) => {
      nodes.forEach(node => {
        if (level < initialExpandedLevels) ids.push(node.Code);
        if (node.children) collect(node.children, level + 1);
      });
    };
    collect(data, 0);
    setExpandedNodes(ids);
  }, [data, initialExpandedLevels]);
  
  const toggleNode = (nodeId) => {
    setExpandedNodes(prev => 
      prev.includes(nodeId)
        ? prev.filter(id => id !== nodeId)
        : [...prev, nodeId]
    );
  };
  
  const handleSelect = (node) => {
    setSelectedNode(node);
    if (onSelectAccount) {
      onSelectAccount(node);
    }
  };
  
  if (!data) {
    return (
      <TreeContainer>
        <TreeHeader>
          <h2>Loading accounts...</h2>
        </TreeHeader>
      </TreeContainer>
    );
  }
  
  return (
    <TreeContainer>
      <TreeHeader>
        <h2>
          <AccountTreeIcon style={{ fontSize: '1.5rem' }} />
          Chart of Accounts
        </h2>
      </TreeHeader>
      
      <TreeList>
        {data.map((node) => (
          <TreeNodeComponent
            key={node.Code}
            node={node}
            level={0}
            onSelect={handleSelect}
            selectedNode={selectedNode}
            expandedNodes={expandedNodes}
            toggleNode={toggleNode}
          />
        ))}
      </TreeList>
    </TreeContainer>
  );
};

export default ModernAccountTreeView;
